<template>
  <div v-if="loading" class="d-flex flex-column align-center justify-center pa-10">
    <v-progress-circular indeterminate color="primary" size="64" />
    <div class="mt-4">Loading... Please wait</div>
  </div>

  <v-expansion-panels accordion flat v-else>
    <v-expansion-panel
      v-for="item in items"
      :key="item.id"
    >
      <v-expansion-panel-header :style="rowStyle(item)">
        <div class="d-flex align-center justify-space-between w-100">
          <div class="d-flex align-center">
            <v-checkbox
              :input-value="selectedIds.includes(item.id)"
              hide-details
              class="mt-0 pt-0 mr-2"
              v-if="item.marked_at === null"
              @click.stop="toggleSelected(item.id)"
            />

            <div class="d-flex flex-column">
              <div class="font-weight-medium">
                <template v-if="item.salesOrder">
                  {{ item.salesOrder.so_number }}
                </template>
                <template v-else-if="item.sales_order">
                  {{ item.sales_order.so_number }}
                </template>
                <template v-else>
                  CP: {{ item.company_name }} • {{ item.customer_name }} #{{ item.id }}
                </template>
              </div>

              <div class="text--secondary" style="font-size: 12px;">
                {{ item.company_name }} • {{ item.customer_name }} • {{ formatDate(item.order_date) }}
              </div>

              <div
                v-if="item?.meta?.delivery_date"
                :class="isDueWithinDays(item, 5) ? 'text--error' : 'text--secondary'"
                style="font-size: 12px;"
              >
                Delivery date: {{ item.meta.delivery_date }}
              </div>
            </div>
          </div>

          <div class="d-flex align-center">
            <v-chip
              x-small
              label
              dark
              class="mr-2"
              :color="salesGroup(item).color"
              @click.stop
            >
              <v-icon left x-small>{{ salesGroup(item).icon }}</v-icon>
              {{ salesGroup(item).label }}
            </v-chip>

            <v-menu
              offset-y
              bottom
              :close-on-content-click="false"
              :value="!!colorMenus[item.id]"
              @input="setColorMenu(item.id, $event)"
            >
              <template v-slot:activator="{ on, attrs }">
                <v-chip
                  x-small
                  outlined
                  class="mr-2"
                  v-bind="attrs"
                  v-on="on"
                  @click.stop
                >
                  <v-avatar
                    left
                    size="10"
                    :style="{ backgroundColor: palette[(item?.meta?.color || 'default')] || '#E5E7EB' }"
                  />
                  {{ item?.meta?.color || 'default' }}
                </v-chip>
              </template>

              <v-card class="pa-2" width="260">
                <div class="d-flex flex-wrap">
                  <v-btn
                    v-for="(key, idx) in colorOrder"
                    :key="key"
                    class="ma-1 swatch-btn"
                    small
                    depressed
                    :style="{ backgroundColor: palette[key] || '#E5E7EB' }"
                    @click="setRowColor(item, key)"
                    :title="key"
                  >
                    <span
                      v-if="(item?.meta?.color || 'default') !== key"
                      class="swatch-num"
                    >
                      {{ idx + 1 }}
                    </span>

                    <v-icon v-else small class="swatch-check">mdi-check</v-icon>
                  </v-btn>

                  <v-btn class="ma-1" small outlined @click="setRowColor(item, 'default')">
                    Clear
                  </v-btn>
                </div>
              </v-card>
            </v-menu>

            <v-tooltip bottom v-if="item.user">
              <template v-slot:activator="{ on, attrs }">
                <v-chip
                  small
                  outlined
                  color="primary"
                  class="mr-2"
                  v-bind="attrs"
                  v-on="on"
                  @click.stop
                >
                  {{ item.user.christian_name1 }}
                </v-chip>
              </template>
              <span>
                {{ item.user.email }} - {{ item.user.surname }}
                {{ item.user.christian_name1 }} {{ item.user.christian_name2 ?? '' }}
              </span>
            </v-tooltip>

            <v-btn icon @click.stop="requestEdit(item)">
              <v-icon>mdi-pencil</v-icon>
            </v-btn>
          </div>
        </div>
      </v-expansion-panel-header>

      <v-expansion-panel-content :style="rowStyle(item)">
        <v-list dense>

          <v-subheader>Basic Information</v-subheader>
          <v-list-item>
            <v-list-item-content>
              <div><b>CP: {{ item.company_name }}</b></div>
              <div><b>{{ item.customer_name }}</b></div>
              <div>{{ item.customer_email }}</div>

              <div>
                <v-tooltip bottom>
                  <template v-slot:activator="{ on, attrs }">
                    <span v-bind="attrs" v-on="on"><b>PN:</b></span>
                  </template>
                  <span>Phone Number</span>
                </v-tooltip>
                {{ item.customer_phone }}
              </div>

              <div class="mt-2">
                <v-tooltip bottom>
                  <template v-slot:activator="{ on, attrs }">
                    <span v-bind="attrs" v-on="on"><b>TN:</b></span>
                  </template>
                  <span>Tax Number</span>
                </v-tooltip>
                {{ item.tax_number }}
              </div>

              <div class="mt-2" v-if="item.billing_address && item.billing_address.billing">
                <div>
                  {{ item.billing_address.billing.postal_code }}
                  {{ item.billing_address.billing.city }},
                </div>
                <div>{{ item.billing_address.billing.address }}</div>
                <div>{{ (item.billing_address.billing.country) }}</div>
              </div>
            </v-list-item-content>
          </v-list-item>

          <v-subheader>Shipping address</v-subheader>
          <v-list-item>
            <v-list-item-content>
              <template v-if="item.shipping_address && item.shipping_address.shipping">
                <div>
                  {{ item.shipping_address.shipping.postal_code }}
                  {{ item.shipping_address.shipping.city }},
                </div>
                <div>{{ item.shipping_address.shipping.address }}</div>
                <div>{{ formatCountry(item.shipping_address.shipping.country) }}</div>
              </template>
              <template v-else>
                -
              </template>
            </v-list-item-content>
          </v-list-item>

          <v-subheader>Item(s)</v-subheader>
          <v-list-item>
            <v-list-item-content>
              <template v-if="item.items_text && String(item.items_text).trim().length">
                <div style="white-space: pre-line;">{{ item.items_text }}</div>
              </template>
              <template v-else>
                <div v-for="it in (item.items || [])" :key="it.id">
                  {{ it.qty }} {{ it.unit ?? 'db' }} - {{ it.reference }} - {{ it.item_name }}
                </div>
              </template>

              <div class="mt-2">
                <template v-if="item.salesOrder">
                  <router-link :to="`/sales/salesorders/${item.so_id}`" target="_blank">
                    {{ item.salesOrder.so_number }}
                  </router-link>
                </template>
                <template v-else-if="item.sales_order">
                  <router-link :to="`/sales/salesorders/${item.sales_order.id}`" target="_blank">
                    {{ item.sales_order.so_number }}
                  </router-link>
                </template>
              </div>

              <div v-if="item?.meta?.delivery_date" class="mt-2">
                <div
                  :class="isDueWithinDays(item, 5) ? 'text--error' : 'text--secondary'"
                >
                  Delivery date: {{ item.meta.delivery_date }}
                </div>
              </div>
            </v-list-item-content>
          </v-list-item>

          <v-subheader>Order Date</v-subheader>
          <v-list-item>
            <v-list-item-content>
              {{ formatDate(item.order_date) }}
            </v-list-item-content>
          </v-list-item>

          <v-subheader>Comment</v-subheader>
          <v-list-item>
            <v-list-item-content>
              <div style="white-space: pre-line;">{{ item.comment }}</div>
            </v-list-item-content>
          </v-list-item>

        </v-list>
      </v-expansion-panel-content>
    </v-expansion-panel>
  </v-expansion-panels>
</template>
<script>
//import { mapGetters, mapActions } from "vuex";

export const ENDPOINT = "sales/Orders/";

export default {
  name: "MobileOrders",

  props: {
    items: { type: Array, default: () => [] },
    loading: { type: Boolean, default: false },

    selectedIds: { type: Array, default: () => [] },

    colorOrder: { type: Array, required: true },
    palette: { type: Object, required: true },
  },

  data() {
    return {
      colorMenus: {},

      expanded: [],
      singleExpand: false,
    };
  },

  methods: {
    toggleSelected(id) {
      const next = this.selectedIds.includes(id)
        ? this.selectedIds.filter(x => x !== id)
        : [...this.selectedIds, id];

      this.$emit("update:selectedIds", next);
    },

    requestEdit(item) {
      this.$emit("edit", item);
    },

    setRowColor(item, key) {
      this.$emit("color-change", item,key || "default" );
      this.$set(this.colorMenus, item.id, false);
    },

    setColorMenu(id, val) {
      this.$set(this.colorMenus, id, !!val);
    },

    formatDate(d) {
        if (!d) return '-';
        try {
        return new Date(d).toLocaleDateString();
        } catch (e) {
        return String(d);
        }
    },
    
    formatCountry(c) {
      return c || '-';
    },

    salesGroup(item) {
      const sp = Number(item?.meta?.sales_point_id ?? 0);

      if ([2, 5, 9].includes(sp)) {
        return { label: 'Livestock', icon: 'mdi-cow', color: 'teal' };
      }
      if (sp === 7) {
        return { label: 'Ironmongery', icon: 'mdi-wrench', color: 'blue-grey' };
      }
      return { label: 'Other', icon: 'mdi-help-circle-outline', color: 'grey' };
    },

    parseDeliveryDate(v) {
      if (!v) return null;
      const m = String(v).match(/(\d{4})\D+(\d{1,2})\D+(\d{1,2})/);
      if (!m) return null;
      return new Date(parseInt(m[1],10), parseInt(m[2],10)-1, parseInt(m[3],10));
    },

    isDueWithinDays(item, days = 5) {
      const target = this.parseDeliveryDate(item?.meta?.delivery_date);
      if (!target) return false;

      const today = new Date();
      const utcDay = (d) => Date.UTC(d.getFullYear(), d.getMonth(), d.getDate());
      const diffDays = Math.round((utcDay(target) - utcDay(today)) / 86400000);

      return Math.abs(diffDays) <= days;
    },

    hexToRgba(hex, alpha = 0.1) {
      const h = String(hex || '').replace('#','');
      if (h.length !== 6) return `rgba(0,0,0,0)`;
      const r = parseInt(h.substring(0,2), 16);
      const g = parseInt(h.substring(2,4), 16);
      const b = parseInt(h.substring(4,6), 16);
      return `rgba(${r}, ${g}, ${b}, ${alpha})`;
    },

    rowStyle(item) {
      if (this.isDueWithinDays(item, 5)) {
        return { backgroundColor: this.hexToRgba(this.palette.red, 0.10) };
      }

      const key = item?.meta?.color ?? 'default';
      const hex = this.palette[key];

      if (!hex || key === 'default') return {};

      return { backgroundColor: this.hexToRgba(hex, 0.10) };
    },
  },
};
</script>

<style>

</style>